import React, { useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import { ChevronRight, ChevronDown, Check, Loader } from "lucide-react";
import { alpha } from "@mui/material/styles";
import { tokens } from "@/theme/tokens";
import type { TurnStep } from "../hooks/useStreamingTurn";

export interface StepTimelineProps {
  steps: TurnStep[];
}

/** Collapsed "N steps · $X" summary of the agent loop's tool-iterations for
 *  this turn, above the answer. Click to expand the per-iteration chip list.
 *  Mirrors WorkingPill's collapsed-summary/expandable-list shape. */
export function StepTimeline({ steps }: StepTimelineProps) {
  const [open, setOpen] = useState(false);
  if (steps.length === 0) return null;

  const n = steps.length;
  // Latest known cumulative cost across steps (only done steps carry one).
  const lastCost = [...steps].reverse().find((s) => s.cumulativeCostUsd != null)?.cumulativeCostUsd;
  const costLabel = typeof lastCost === "number" ? ` · $${lastCost.toFixed(2)}` : "";
  const summary = `${n} step${n === 1 ? "" : "s"}${costLabel}`;

  return (
    <Box sx={{ my: 0.75 }} data-ph-scope="step_timeline">
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="step timeline"
        data-ph={open ? "collapse" : "expand"}
        aria-expanded={open}
        sx={{
          display: "inline-flex", alignItems: "center", gap: 0.75,
          border: 1, borderColor: "divider", borderRadius: 999,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
          color: "text.secondary", cursor: "pointer", px: 1.25, py: 0.5,
          font: "inherit", fontSize: 12,
          maxWidth: "100%", minWidth: 0,
          transition: (t) => `border-color ${t.transitions.duration.shorter}ms ease, background-color ${t.transitions.duration.shorter}ms ease`,
          "&:hover": { borderColor: "primary.main" },
          "&:focus-visible": (t) => ({
            outline: "none",
            boxShadow: t.palette.mode === "light" ? tokens.focusRing.light : tokens.focusRing.dark,
          }),
        }}
      >
        <Box component="span" sx={{ flexShrink: 0, width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} />
        <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {summary}
        </Box>
        <Box component="span" sx={{ flexShrink: 0, display: "inline-flex" }}>
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </Box>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 0.75, ml: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {steps.map((s) => (
            <Box
              key={s.iteration}
              data-testid="step-chip"
              sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, minWidth: 0, color: "text.secondary" }}
            >
              <Box component="span" sx={{ flexShrink: 0, display: "inline-flex", mt: "2px" }}>
                {s.done ? <Check size={12} /> : <Loader size={12} className="wp-spin" />}
              </Box>
              <Typography variant="caption" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                Step {s.iteration + 1} · {s.model}
                {s.toolNames.length > 0 ? ` · ${s.toolNames.join(", ")}` : ""}
                {typeof s.iterCostUsd === "number" ? ` · $${s.iterCostUsd.toFixed(3)}` : ""}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
